import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Paper, Chip, Grid, TextField } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import TimelineIcon from '@mui/icons-material/Timeline';

const quickValue = [
  {
    icon: <SearchIcon fontSize="small" />,
    title: 'Instant ticker discovery',
  },
  {
    icon: <BookmarkIcon fontSize="small" />,
    title: 'Institutional watchlists',
  },
  {
    icon: <CompareArrowsIcon fontSize="small" />,
    title: 'Cross-company consistency',
  },
];

const surfaceCards = [
  {
    icon: <TimelineIcon fontSize="small" />,
    title: 'Narrative Timeline',
    body: 'Follow multi-year revenue, margin, and cash-flow shifts without switching tools.',
  },
  {
    icon: <InsightsIcon fontSize="small" />,
    title: 'Quality Signals',
    body: 'Detect balance-sheet stress and operating momentum with standardized metrics.',
  },
  {
    icon: <TrendingUpIcon fontSize="small" />,
    title: 'Execution Speed',
    body: 'Reduce analysis time from hours to minutes with comparison-ready company profiles.',
  },
];

const marketPulse = [
  { label: 'Universe', value: '12K+', delta: 'Listed companies' },
  { label: 'Refresh', value: 'Daily', delta: 'Fundamentals + pricing' },
  { label: 'Comparability', value: '99.9%', delta: 'Mapped fields' },
  { label: 'Research Lift', value: '3.1x', delta: 'Workflow acceleration' },
];

const subscriptionPlans = [
  {
    key: 'plan_1m',
    name: '1 Month Access',
    price: '$16',
    note: 'One-time payment',
  },
  {
    key: 'plan_3m',
    name: '3 Months Access',
    price: '$30',
    note: 'One-time payment',
  },
  {
    key: 'plan_6m',
    name: '6 Months Access',
    price: '$50',
    note: 'One-time payment',
  },
  {
    key: 'plan_12m',
    name: '12 Months / 1 Year',
    price: '$79.99',
    note: 'Best yearly value',
    featured: true,
    badge: 'Most Popular',
  },
  {
    key: 'plan_monthly',
    name: 'Monthly Until Canceled',
    price: '$8.99/mo',
    note: 'Auto-renews until you quit',
    badge: 'Flexible',
  },
];

const nvdaSnapshot = [
  { label: 'Revenue (Latest Quarter)', value: '$81.61B', delta: '+43.17% from previous quarter' },
  { label: 'Operating Income (Latest Quarter)', value: '$53.54B', delta: '+48.67% from previous quarter' },
  { label: 'Profit Margin', value: '52.48%', delta: '+3.84% from previous quarter' },
  { label: 'Market Cap (USD)', value: '$5.11T', delta: 'Computed: Shares × Price' },
  { label: 'Current Share Price', value: '$211.14', delta: '' },
  { label: 'P/E Valuation (Quick View)', value: '36.63x', delta: 'Proxy used: Market Cap / Operating Income (TTM)' },
];

const nvdaRevenueBars = [
  { q: 'Q1', value: 26.0 },
  { q: 'Q2', value: 30.0 },
  { q: 'Q3', value: 35.1 },
  { q: 'Q4', value: 60.9 },
  { q: 'Q1', value: 71.3 },
  { q: 'Q2', value: 80.0 },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const PENDING_CHECKOUT_PLAN_KEY = 'pending_checkout_plan_key';
const PENDING_DISCOUNT_CODE_KEY = 'pending_discount_code';
const AUTH_CALLBACK_RETRY_KEY = 'auth_callback_retry_once';
const MAP_LANDING_SHOWN_KEY = 'map_landing_shown';
const VALID_PLAN_KEYS = new Set(subscriptionPlans.map((plan) => plan.key));
const ENTITLEMENT_CACHE_KEY = 'billing_entitlement_active_until';
const ENTITLEMENT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const ENTITLEMENT_MAX_ATTEMPTS = 4;

const delay = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const getCachedEntitlementActive = () => {
  try {
    const raw = window.localStorage.getItem(ENTITLEMENT_CACHE_KEY);
    const until = Number.parseInt(String(raw || ''), 10);
    return Number.isFinite(until) && until > Date.now();
  } catch (error) {
    return false;
  }
};

const setCachedEntitlementActive = () => {
  try {
    window.localStorage.setItem(
      ENTITLEMENT_CACHE_KEY,
      String(Date.now() + ENTITLEMENT_CACHE_TTL_MS),
    );
  } catch (error) {
    // Ignore storage failures.
  }
};

const clearCachedEntitlementActive = () => {
  try {
    window.localStorage.removeItem(ENTITLEMENT_CACHE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
};

const toFiniteNumber = (value) => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCompactCurrency = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatMarketCap = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  const trillions = value / 1e12;
  if (trillions >= 1) return `$${trillions.toFixed(2)}T`;
  const billions = value / 1e9;
  if (billions >= 1) return `$${billions.toFixed(2)}B`;
  return formatCompactCurrency(value);
};

const getQuarterLabel = (period, idx) => {
  const dt = new Date(period);
  if (Number.isNaN(dt.getTime())) return `Q${(idx % 4) + 1}`;
  return `Q${Math.floor(dt.getUTCMonth() / 3) + 1}`;
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user, getAccessTokenSilently, error: authSdkError } = useAuth0();
  const authRedirectUri = window.location.origin;
  const queryParams = useMemo(() => new URLSearchParams(window.location.search || ''), [location.search]);
  const hasAuthCode = Boolean(queryParams.get('code'));
  const hasAuthState = Boolean(queryParams.get('state'));
  const authErrorParam = queryParams.get('error');
  const authErrorDescriptionParam = queryParams.get('error_description');
  const hasAuthCallbackParams = hasAuthCode && hasAuthState;
  const [nvdaCardData, setNvdaCardData] = useState(nvdaSnapshot);
  const [nvdaChartBars, setNvdaChartBars] = useState(nvdaRevenueBars);
  const [nvdaYoyText, setNvdaYoyText] = useState('+43.17% from previous quarter');
  const [checkoutPlanKey, setCheckoutPlanKey] = useState('');
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountCodeBusy, setDiscountCodeBusy] = useState(false);
  const [discountCodeError, setDiscountCodeError] = useState('');
  const [discountCodeNotice, setDiscountCodeNotice] = useState('');
  const [entitlementChecked, setEntitlementChecked] = useState(getCachedEntitlementActive());
  const [hasPaidAccess, setHasPaidAccess] = useState(getCachedEntitlementActive());
  const normalizedUserEmail = String(user?.email || '').trim().toLowerCase();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();
  const testBypassEmailRaw = String(
    runtimeEnv.TEST_BILLING_BYPASS_EMAILS || process.env.REACT_APP_TEST_BILLING_BYPASS_EMAILS || '',
  ).trim();
  const testBypassEmails = useMemo(() => (
    new Set(
      testBypassEmailRaw
        .split(',')
        .map((email) => String(email || '').trim().toLowerCase())
        .filter(Boolean),
    )
  ), [testBypassEmailRaw]);
  const isTestBypassUser = Boolean(normalizedUserEmail) && testBypassEmails.has(normalizedUserEmail);

  const getBillingAccessToken = useCallback(async () => {
    const authorizationParams = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    return getAccessTokenSilently({ authorizationParams });
  }, [authAudience, getAccessTokenSilently]);

  const isPublicLegalRoute = String(location.pathname || '').startsWith('/legal/');
  const isBillingSuccessRoute = String(location.pathname || '') === '/billing/success';

  // Land on the full-page country map once per browser session after sign-in.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      try {
        window.sessionStorage.removeItem(MAP_LANDING_SHOWN_KEY);
      } catch (error) {
        // Ignore storage failures.
      }
      return;
    }
    if (isPublicLegalRoute || isBillingSuccessRoute) return;
    if (hasAuthCallbackParams) return;

    const path = String(location.pathname || '');
    if (path.startsWith('/legal/') || path.startsWith('/billing/')) return;

    try {
      if (window.sessionStorage.getItem(MAP_LANDING_SHOWN_KEY) === '1') return;
      window.sessionStorage.setItem(MAP_LANDING_SHOWN_KEY, '1');
    } catch (error) {
      return;
    }

    if (path === '/map') return;
    navigate('/map', { replace: true });
  }, [
    isAuthenticated,
    isLoading,
    isPublicLegalRoute,
    isBillingSuccessRoute,
    hasAuthCallbackParams,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (isPublicLegalRoute) return;
    if (isLoading || isAuthenticated) {
      try {
        window.sessionStorage.removeItem(AUTH_CALLBACK_RETRY_KEY);
      } catch (error) {
        // Ignore storage failures.
      }
      return;
    }

    if (!hasAuthCallbackParams && !authErrorParam) return;

    let hasRetried = false;
    try {
      hasRetried = window.sessionStorage.getItem(AUTH_CALLBACK_RETRY_KEY) === '1';
    } catch (error) {
      hasRetried = false;
    }

    if (!hasRetried && hasAuthCallbackParams) {
      try {
        window.sessionStorage.setItem(AUTH_CALLBACK_RETRY_KEY, '1');
      } catch (error) {
        // Ignore storage failures.
      }

      loginWithRedirect({
        appState: { returnTo: window.location.pathname || '/' },
        authorizationParams: { redirect_uri: authRedirectUri },
      });
      return;
    }

    try {
      window.sessionStorage.removeItem(AUTH_CALLBACK_RETRY_KEY);
    } catch (error) {
      // Ignore storage failures.
    }

    window.history.replaceState({}, document.title, window.location.pathname || '/');
  }, [
    authErrorParam,
    authRedirectUri,
    hasAuthCallbackParams,
    isAuthenticated,
    isLoading,
    isPublicLegalRoute,
    loginWithRedirect,
  ]);

  const fallbackMap = useMemo(() => {
    const map = {};
    nvdaSnapshot.forEach((entry) => {
      map[entry.label] = entry;
    });
    return map;
  }, []);

  useEffect(() => {
    if (isPublicLegalRoute) return;

    let active = true;

    const fetchNvdaCardData = async () => {
      try {
        const [stockResult, financialResult, overviewResult] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/api/stock-price/NVDA`),
          fetch(`${API_BASE_URL}/api/financial-data/NVDA`),
          fetch(`${API_BASE_URL}/api/company-overview/NVDA`),
        ]);

        let stockPayload = null;
        if (stockResult.status === 'fulfilled' && stockResult.value.ok) {
          stockPayload = await stockResult.value.json();
        }

        let financialPayload = null;
        if (financialResult.status === 'fulfilled' && financialResult.value.ok) {
          financialPayload = await financialResult.value.json();
        }

        let overviewPayload = null;
        if (overviewResult.status === 'fulfilled' && overviewResult.value.ok) {
          overviewPayload = await overviewResult.value.json();
        }

        const financialRows = Array.isArray(financialPayload?.data)
          ? financialPayload.data
          : (Array.isArray(financialPayload) ? financialPayload : []);

        const revenueRows = financialRows
          .map((row) => ({
            period: row?.period,
            revenue: toFiniteNumber(row?.Revenue ?? row?.revenue ?? row?.total_revenue),
            operatingIncome: toFiniteNumber(row?.['Operating Income'] ?? row?.operating_income ?? row?.operatingIncome),
            grossProfit: toFiniteNumber(row?.['Gross Profit'] ?? row?.gross_profit ?? row?.grossProfit),
          }))
          .filter((row) => Number.isFinite(row.revenue) && row.revenue > 0)
          .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());

        if (active && revenueRows.length > 0) {
          const latestSix = revenueRows.slice(-6);
          const nextBars = latestSix.map((row, idx) => ({
            q: getQuarterLabel(row.period, idx),
            value: Number((row.revenue / 1e9).toFixed(1)),
          }));
          setNvdaChartBars(nextBars);

          const latest = latestSix[latestSix.length - 1]?.value;
          const previous = latestSix.length > 1 ? latestSix[latestSix.length - 2]?.value : null;
          if (Number.isFinite(latest) && Number.isFinite(previous) && previous > 0) {
            const qoqPct = ((latest - previous) / previous) * 100;
            setNvdaYoyText(`${qoqPct >= 0 ? '+' : ''}${qoqPct.toFixed(2)}% from previous quarter`);
          }
        }

        if (!active) return;

        const price = toFiniteNumber(stockPayload?.price);
        const marketCapFromQuote = toFiniteNumber(stockPayload?.marketCap);
        const outstandingShares = toFiniteNumber(overviewPayload?.outstanding_shares);
        const marketCapFromOverview = toFiniteNumber(overviewPayload?.market_cap);
        const computedMarketCap =
          Number.isFinite(price) && Number.isFinite(outstandingShares)
            ? price * outstandingShares
            : null;
        const marketCap = Number.isFinite(marketCapFromQuote)
          ? marketCapFromQuote
          : (Number.isFinite(marketCapFromOverview) ? marketCapFromOverview : computedMarketCap);
        const changeValue = toFiniteNumber(stockPayload?.change?.value);
        const trailingPe = toFiniteNumber(stockPayload?.trailingPE);
        const forwardPe = toFiniteNumber(stockPayload?.forwardPE);

        const latestPeriod = revenueRows.length > 0 ? revenueRows[revenueRows.length - 1] : null;
        const previousPeriod = revenueRows.length > 1 ? revenueRows[revenueRows.length - 2] : null;

        const latestRevenue = latestPeriod?.revenue ?? null;
        const previousRevenue = previousPeriod?.revenue ?? null;

        const latestOperatingIncome = latestPeriod?.operatingIncome ?? null;
        const previousOperatingIncome = previousPeriod?.operatingIncome ?? null;

        const latestProfitMargin = Number.isFinite(latestOperatingIncome) && Number.isFinite(latestRevenue) && latestRevenue > 0
          ? (latestOperatingIncome / latestRevenue) * 100
          : null;
        const previousProfitMargin = Number.isFinite(previousOperatingIncome) && Number.isFinite(previousRevenue) && previousRevenue > 0
          ? (previousOperatingIncome / previousRevenue) * 100
          : null;

        const revenueQoq = Number.isFinite(latestRevenue) && Number.isFinite(previousRevenue) && previousRevenue > 0
          ? ((latestRevenue - previousRevenue) / previousRevenue) * 100
          : null;
        const operatingQoq = Number.isFinite(latestOperatingIncome) && Number.isFinite(previousOperatingIncome) && previousOperatingIncome > 0
          ? ((latestOperatingIncome - previousOperatingIncome) / previousOperatingIncome) * 100
          : null;
        const marginDeltaPoints = Number.isFinite(latestProfitMargin) && Number.isFinite(previousProfitMargin)
          ? (latestProfitMargin - previousProfitMargin)
          : null;

        const ttmRows = revenueRows
          .filter((row) => Number.isFinite(row.operatingIncome))
          .slice(-4);
        const ttmOperatingIncome = ttmRows.length > 0
          ? ttmRows.reduce((sum, row) => sum + row.operatingIncome, 0)
          : null;
        const operatingIncomeMultiple = Number.isFinite(marketCap) && Number.isFinite(ttmOperatingIncome) && ttmOperatingIncome > 0
          ? marketCap / ttmOperatingIncome
          : null;

        setNvdaCardData([
          {
            label: 'Revenue (Latest Quarter)',
            value: Number.isFinite(latestRevenue) ? formatCompactCurrency(latestRevenue) : fallbackMap['Revenue (Latest Quarter)'].value,
            delta: Number.isFinite(revenueQoq)
              ? `${revenueQoq >= 0 ? '+' : ''}${revenueQoq.toFixed(2)}% from previous quarter`
              : fallbackMap['Revenue (Latest Quarter)'].delta,
          },
          {
            label: 'Operating Income (Latest Quarter)',
            value: Number.isFinite(latestOperatingIncome) ? formatCompactCurrency(latestOperatingIncome) : fallbackMap['Operating Income (Latest Quarter)'].value,
            delta: Number.isFinite(operatingQoq)
              ? `${operatingQoq >= 0 ? '+' : ''}${operatingQoq.toFixed(2)}% from previous quarter`
              : fallbackMap['Operating Income (Latest Quarter)'].delta,
          },
          {
            label: 'Profit Margin',
            value: Number.isFinite(latestProfitMargin) ? `${latestProfitMargin.toFixed(2)}%` : fallbackMap['Profit Margin'].value,
            delta: Number.isFinite(marginDeltaPoints)
              ? `${marginDeltaPoints >= 0 ? '+' : ''}${marginDeltaPoints.toFixed(2)}% from previous quarter`
              : fallbackMap['Profit Margin'].delta,
          },
          {
            label: 'Market Cap (USD)',
            value: Number.isFinite(marketCap) ? formatMarketCap(marketCap) : fallbackMap['Market Cap (USD)'].value,
            delta: Number.isFinite(marketCapFromQuote)
              ? 'From live quote'
              : (Number.isFinite(marketCapFromOverview)
                ? 'From company overview'
                : (Number.isFinite(computedMarketCap) ? 'Computed: Shares × Price' : fallbackMap['Market Cap (USD)'].delta)),
          },
          {
            label: 'Current Share Price',
            value: Number.isFinite(price)
              ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(price)
              : fallbackMap['Current Share Price'].value,
            delta: Number.isFinite(changeValue) && Math.abs(changeValue) >= 0.005
              ? `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(changeValue)}`
              : fallbackMap['Current Share Price'].delta,
          },
          {
            label: 'P/E Valuation (Quick View)',
            value: Number.isFinite(operatingIncomeMultiple)
              ? `${operatingIncomeMultiple.toFixed(2)}x`
              : (Number.isFinite(trailingPe)
                ? `${trailingPe.toFixed(2)}x`
                : (Number.isFinite(forwardPe) ? `${forwardPe.toFixed(2)}x` : fallbackMap['P/E Valuation (Quick View)'].value)),
            delta: Number.isFinite(operatingIncomeMultiple)
              ? 'Proxy used: Market Cap / Operating Income (TTM)'
              : fallbackMap['P/E Valuation (Quick View)'].delta,
          },
        ]);
      } catch (error) {
        // Keep landing page stable even if upstream data is temporarily unavailable.
      }
    };

    fetchNvdaCardData();
    return () => {
      active = false;
    };
  }, [fallbackMap, isPublicLegalRoute]);

  const startCheckoutForPlan = async (planKey, checkoutUser) => {
    try {
      setCheckoutPlanKey(planKey);
      const accessToken = await getBillingAccessToken();

      const response = await fetch(`${API_BASE_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          planKey,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Unable to start checkout');
      }

      const payload = await response.json();
      if (!payload?.url) {
        throw new Error('Checkout URL was not returned');
      }

      window.location.assign(payload.url);
    } catch (error) {
      window.alert(error.message || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutPlanKey('');
    }
  };

  useEffect(() => {
    if (isPublicLegalRoute) return;

    if (!isAuthenticated || !user?.sub) return;

    const searchParams = new URLSearchParams(window.location.search);
    const planFromQuery = searchParams.get('checkoutPlan');
    const planFromLocalStorage = window.localStorage.getItem(PENDING_CHECKOUT_PLAN_KEY);
    const planFromSessionStorage = window.sessionStorage.getItem(PENDING_CHECKOUT_PLAN_KEY);
    const pendingPlanKey = planFromQuery || planFromLocalStorage || planFromSessionStorage;
    if (!pendingPlanKey || !VALID_PLAN_KEYS.has(pendingPlanKey)) return;

    window.localStorage.removeItem(PENDING_CHECKOUT_PLAN_KEY);
    window.sessionStorage.removeItem(PENDING_CHECKOUT_PLAN_KEY);
    if (planFromQuery) {
      searchParams.delete('checkoutPlan');
      const nextQuery = searchParams.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
    }

    void startCheckoutForPlan(pendingPlanKey, user);
  }, [isAuthenticated, user, isPublicLegalRoute]);

  useEffect(() => {
    if (isPublicLegalRoute || !isBillingSuccessRoute) return;
    if (!isAuthenticated || !user?.sub) return;

    let active = true;

    const confirmCheckoutSession = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const sessionId = String(searchParams.get('session_id') || '').trim();
      if (!sessionId) {
        return;
      }

      try {
        const accessToken = await getBillingAccessToken();
        const response = await fetch(`${API_BASE_URL}/api/billing/confirm-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ sessionId }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!active) return;

        if (!response.ok) {
          return;
        }

        if (payload?.isActive) {
          setHasPaidAccess(true);
          setEntitlementChecked(true);
          window.history.replaceState({}, document.title, '/dashboard');
        }
      } catch (error) {
        // Entitlement fetch effect handles fallback display.
      }
    };

    void confirmCheckoutSession();

    return () => {
      active = false;
    };
  }, [
    isPublicLegalRoute,
    isBillingSuccessRoute,
    isAuthenticated,
    user,
    getBillingAccessToken,
  ]);

  useEffect(() => {
    if (isPublicLegalRoute) return;

    let active = true;

    const checkEntitlement = async () => {
      if (!isAuthenticated || !user?.sub) {
        if (active) {
          setEntitlementChecked(false);
          setHasPaidAccess(false);
        }
        return;
      }

      if (isTestBypassUser) {
        if (active) {
          setHasPaidAccess(true);
          setEntitlementChecked(true);
          setCachedEntitlementActive();
        }
        return;
      }

      let resolved = false;

      for (let attempt = 0; attempt < ENTITLEMENT_MAX_ATTEMPTS; attempt += 1) {
        try {
          const accessToken = await getBillingAccessToken();
          const entitlementUrl = `${API_BASE_URL}/api/billing/entitlement?ts=${Date.now()}`;
          const response = await fetch(entitlementUrl, {
            method: 'GET',
            cache: 'no-store',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
          });

          if (response.status === 304) {
            if (!active) return;
            setEntitlementChecked(true);
            resolved = true;
            break;
          }

          if (!response.ok) {
            throw new Error(`Entitlement request failed with status ${response.status}`);
          }

          const payload = await response.json().catch(() => ({}));
          if (!active) return;

          const isActive = Boolean(payload?.isActive || payload?.isExempt);
          setHasPaidAccess(isActive);
          setEntitlementChecked(true);
          if (isActive) {
            setCachedEntitlementActive();
          } else {
            clearCachedEntitlementActive();
          }
          resolved = true;
          break;
        } catch (error) {
          if (attempt < ENTITLEMENT_MAX_ATTEMPTS - 1) {
            await delay(350 * (attempt + 1));
            continue;
          }
        }
      }

      if (!active || resolved) return;

      if (getCachedEntitlementActive()) {
        setHasPaidAccess(true);
        setEntitlementChecked(true);
        return;
      }

      setHasPaidAccess(false);
      setEntitlementChecked(true);
    };

    checkEntitlement();

    return () => {
      active = false;
    };
  }, [isAuthenticated, user, getBillingAccessToken, isPublicLegalRoute, isTestBypassUser]);

  const handleStartCheckout = async (planKey) => {
    if (!isAuthenticated) {
      window.localStorage.setItem(PENDING_CHECKOUT_PLAN_KEY, planKey);
      window.sessionStorage.setItem(PENDING_CHECKOUT_PLAN_KEY, planKey);
      const returnToWithPlan = `${window.location.pathname}?checkoutPlan=${encodeURIComponent(planKey)}`;
      await loginWithRedirect({
        appState: { returnTo: returnToWithPlan, checkoutPlanKey: planKey },
      });
      return;
    }

    await startCheckoutForPlan(planKey, user);
  };

  const redeemDiscountCode = useCallback(async (rawCode) => {
    const normalizedCode = String(rawCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '').trim();
    if (!normalizedCode) {
      setDiscountCodeError('Enter a valid discount code.');
      setDiscountCodeNotice('');
      return;
    }

    setDiscountCodeError('');
    setDiscountCodeNotice('');

    if (!isAuthenticated) {
      try {
        window.localStorage.setItem(PENDING_DISCOUNT_CODE_KEY, normalizedCode);
        window.sessionStorage.setItem(PENDING_DISCOUNT_CODE_KEY, normalizedCode);
      } catch (storageError) {
        // Ignore storage failures.
      }
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname || '/' },
      });
      return;
    }

    setDiscountCodeBusy(true);
    try {
      const accessToken = await getBillingAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/billing/redeem-discount-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ code: normalizedCode }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to redeem discount code');
      }

      if (payload?.alreadyRedeemed) {
        setDiscountCodeNotice(`Code already redeemed for ${payload?.planLabel || 'your plan'}.`);
      } else {
        setDiscountCodeNotice(`Code applied. ${payload?.planLabel || 'Plan'} access has been enabled.`);
      }
      setDiscountCodeInput('');
      setHasPaidAccess(true);
      setEntitlementChecked(true);
      setCachedEntitlementActive();
    } catch (error) {
      setDiscountCodeError(error.message || 'Failed to redeem discount code');
    } finally {
      setDiscountCodeBusy(false);
    }
  }, [getBillingAccessToken, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;

    const query = new URLSearchParams(window.location.search || '');
    const codeFromQuery = String(query.get('discountCode') || '').trim();
    const codeFromLocalStorage = String(window.localStorage.getItem(PENDING_DISCOUNT_CODE_KEY) || '').trim();
    const codeFromSessionStorage = String(window.sessionStorage.getItem(PENDING_DISCOUNT_CODE_KEY) || '').trim();
    const pendingCode = codeFromQuery || codeFromLocalStorage || codeFromSessionStorage;
    if (!pendingCode) return;

    try {
      window.localStorage.removeItem(PENDING_DISCOUNT_CODE_KEY);
      window.sessionStorage.removeItem(PENDING_DISCOUNT_CODE_KEY);
    } catch (storageError) {
      // Ignore storage failures.
    }

    if (codeFromQuery) {
      query.delete('discountCode');
      const nextQuery = query.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
    }

    void redeemDiscountCode(pendingCode);
  }, [isAuthenticated, redeemDiscountCode, user]);

  const discountCodePanel = (
    <Box
      sx={{
        mt: 1.2,
        p: 1.2,
        borderRadius: 2,
        border: '1px solid rgba(122,166,240,0.34)',
        background: 'rgba(14,33,72,0.58)',
      }}
    >
      <Typography sx={{ color: '#dceaff', fontWeight: 700, fontSize: '0.9rem' }}>
        Have a discount code?
      </Typography>
      <Typography sx={{ mt: 0.3, color: '#9dbde9', fontSize: '0.78rem', lineHeight: 1.45 }}>
        Redeem a code to unlock plan access without entering payment details.
      </Typography>
      <Box
        sx={{
          mt: 0.8,
          display: 'flex',
          gap: 0.8,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Enter code"
          value={discountCodeInput}
          onChange={(event) => setDiscountCodeInput(event.target.value.toUpperCase())}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void redeemDiscountCode(discountCodeInput);
            }
          }}
          inputProps={{ maxLength: 32 }}
          sx={{
            '& .MuiInputBase-root': {
              color: '#ecf4ff',
              backgroundColor: 'rgba(9,20,44,0.85)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(126,168,243,0.4)',
            },
            '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(154,193,255,0.75)',
            },
            '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(93,191,237,0.9)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#88abd9',
              opacity: 1,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={() => void redeemDiscountCode(discountCodeInput)}
          disabled={discountCodeBusy || !discountCodeInput.trim()}
          sx={{
            minWidth: { xs: '100%', sm: 140 },
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1f63ff 0%, #19a2d6 45%, #11b89e 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1b58e2 0%, #168fc0 45%, #0f9f89 100%)',
            },
          }}
        >
          {discountCodeBusy ? 'Applying...' : 'Apply Code'}
        </Button>
      </Box>
      {discountCodeError ? (
        <Typography sx={{ mt: 0.65, color: '#ffb7b7', fontSize: '0.76rem' }}>{discountCodeError}</Typography>
      ) : null}
      {discountCodeNotice ? (
        <Typography sx={{ mt: 0.65, color: '#9ef0dd', fontSize: '0.76rem' }}>{discountCodeNotice}</Typography>
      ) : null}
    </Box>
  );

  if (isPublicLegalRoute) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          background: 'linear-gradient(135deg, #081126 0%, #0f1f45 55%, #112b5e 100%)',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (isAuthenticated && !entitlementChecked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          background: 'linear-gradient(135deg, #081126 0%, #0f1f45 55%, #112b5e 100%)',
        }}
      >
        <CircularProgress size={48} sx={{ color: 'white' }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          Verifying subscription...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    if ((hasAuthCallbackParams || authErrorParam || authSdkError) && !isLoading) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            background: 'radial-gradient(circle at 20% 0%, #153a78 0%, #0a1a3c 40%, #050c1f 100%)',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 560,
              p: { xs: 2.4, sm: 3 },
              borderRadius: 3,
              border: '1px solid rgba(136,170,245,0.35)',
              background: 'linear-gradient(170deg, rgba(10,23,53,0.92) 0%, rgba(9,18,41,0.9) 100%)',
            }}
          >
            <Typography sx={{ color: '#e8f0ff', fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>
              Login could not be completed
            </Typography>
            <Typography sx={{ color: '#bdd1f1', lineHeight: 1.6, mb: 1.5 }}>
              We received a callback from Auth0, but your browser session was not established. This is often caused by blocked cookies or missing Auth0 Allowed Web Origins/Callback URL for this domain.
            </Typography>
            {authSdkError ? (
              <Typography sx={{ color: '#ffd4d4', fontSize: '0.92rem', mb: 1 }}>
                SDK error: {String(authSdkError?.message || authSdkError)}
              </Typography>
            ) : null}
            {authErrorParam ? (
              <Typography sx={{ color: '#ffd4d4', fontSize: '0.92rem', mb: 2 }}>
                Auth0 error: {authErrorParam}{authErrorDescriptionParam ? ` (${authErrorDescriptionParam})` : ''}
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => loginWithRedirect({ appState: { returnTo: '/map' }, authorizationParams: { redirect_uri: authRedirectUri } })}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Try login again
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  window.history.replaceState({}, document.title, window.location.pathname || '/');
                }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Clear callback URL
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'radial-gradient(circle at 20% 0%, #153a78 0%, #0a1a3c 40%, #050c1f 100%)',
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 2.5, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(112,153,240,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(112,153,240,0.1) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            opacity: 0.22,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 420,
            height: 420,
            right: -120,
            top: -130,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(27,133,233,0.4) 0%, rgba(27,133,233,0) 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 340,
            height: 340,
            left: -120,
            bottom: -120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(16,198,170,0.26) 0%, rgba(16,198,170,0) 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            width: '100%',
            maxWidth: 1340,
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 32px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1d5bff 0%, #11b5d4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 12px 24px rgba(29,91,255,0.38)',
                }}
              >
                <ShowChartIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#e8f0ff', fontSize: '1.1rem' }}>
                Company Analysis
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={() => loginWithRedirect({ appState: { returnTo: '/map' }, authorizationParams: { redirect_uri: authRedirectUri } })}
              sx={{
                borderColor: 'rgba(158,195,255,0.4)',
                color: '#d9e8ff',
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': {
                  borderColor: 'rgba(158,195,255,0.66)',
                  backgroundColor: 'rgba(36,85,173,0.2)',
                },
              }}
            >
              Log in
            </Button>
          </Box>

          <Grid container spacing={{ xs: 2.1, md: 2.8 }} sx={{ flex: 1, alignContent: 'start' }}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4.5,
                  border: '1px solid rgba(136,170,245,0.35)',
                  background: 'linear-gradient(170deg, rgba(10,23,53,0.92) 0%, rgba(9,18,41,0.9) 100%)',
                  p: { xs: 2.3, md: 3.4 },
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 28px 65px rgba(2,7,20,0.55)',
                  height: '100%',
                }}
              >
                <Chip
                  icon={<QueryStatsIcon sx={{ color: '#93c1ff !important' }} />}
                  label="Built for serious equity research"
                  sx={{
                    mb: 1.8,
                    bgcolor: 'rgba(28,60,127,0.45)',
                    color: '#c3dbff',
                    border: '1px solid rgba(141,176,245,0.4)',
                    fontWeight: 700,
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#f0f5ff',
                    fontSize: { xs: '2.2rem', sm: '3.05rem', md: '3.5rem' },
                    lineHeight: 0.95,
                    letterSpacing: '-0.03em',
                    fontFamily: '"Sora", "Manrope", "Space Grotesk", sans-serif',
                    maxWidth: 800,
                  }}
                >
                  Turn raw filings into
                  <br />
                  investment clarity.
                </Typography>

                <Typography
                  sx={{
                    mt: 1.7,
                    color: '#bdd1f1',
                    lineHeight: 1.68,
                    fontSize: { xs: '1.03rem', md: '1.12rem' },
                    maxWidth: 740,
                  }}
                >
                  One workspace for screening, comparing, and monitoring public companies with auditable financial metrics and clean presentation.
                </Typography>

                <Box
                  sx={{
                    mt: 1.4,
                    p: { xs: 1.25, md: 1.55 },
                    borderRadius: 2,
                    border: '1px solid rgba(119,160,238,0.3)',
                    background: 'rgba(12,27,61,0.52)',
                    maxWidth: 720,
                  }}
                >
                  <Typography sx={{ color: '#dbe8ff', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Quick Summary
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: '#c4d6f3', fontSize: '0.97rem', lineHeight: 1.62 }}>
                    Company Analysis helps you research stocks faster by bringing financial statements, key ratios, and trend charts into one clean view so you can compare companies with less manual work.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: 1,
                    px: { xs: 1.25, md: 1.55 },
                    py: 1.05,
                    borderRadius: 2,
                    border: '1px solid rgba(109,152,235,0.26)',
                    background: 'rgba(10,24,54,0.45)',
                    maxWidth: 720,
                  }}
                >
                  <Typography sx={{ color: '#9cb9e9', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    Data Sources
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: '#c7d8f3', fontSize: '0.92rem', lineHeight: 1.6 }}>
                    Financial statements from SEC filings, macro series from FRED and BLS, and market pricing from integrated quote feeds.
                  </Typography>
                </Box>

                <Box sx={{ mt: 2.2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {quickValue.map((item) => (
                    <Chip
                      key={item.title}
                      icon={item.icon}
                      label={item.title}
                      sx={{
                        bgcolor: 'rgba(17,38,81,0.72)',
                        color: '#dde9ff',
                        border: '1px solid rgba(132,167,240,0.32)',
                        fontSize: '0.82rem',
                        height: 30,
                        '& .MuiChip-icon': { color: '#8bbdff' },
                      }}
                    />
                  ))}
                </Box>

                <Grid container spacing={1.2} sx={{ mt: 1.2 }}>
                  {marketPulse.map((metric) => (
                    <Grid item xs={6} sm={3} key={metric.label}>
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: '1px solid rgba(116,156,236,0.3)',
                          background: 'rgba(13,30,67,0.55)',
                        }}
                      >
                        <Typography sx={{ color: '#97b4e7', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {metric.label}
                        </Typography>
                        <Typography sx={{ color: '#f0f5ff', fontSize: '1.12rem', fontWeight: 800, lineHeight: 1.2, mt: 0.22 }}>
                          {metric.value}
                        </Typography>
                        <Typography sx={{ color: '#8cb4ff', fontSize: '0.76rem', mt: 0.22 }}>{metric.delta}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4.5,
                  border: '1px solid rgba(126,164,238,0.3)',
                  background: 'linear-gradient(160deg, rgba(12,28,63,0.94) 0%, rgba(8,17,37,0.94) 100%)',
                  p: { xs: 2.1, md: 2.35 },
                  height: '100%',
                }}
              >
                <Typography sx={{ color: '#eef4ff', fontWeight: 800, fontSize: '1.1rem' }}>No. 1 Stock 2026</Typography>
                <Typography sx={{ color: '#a8c2ec', fontSize: '0.9rem', mt: 0.5, mb: 1.65, lineHeight: 1.55 }}>
                  NVIDIA quick view with the same fast-read metrics and charting style available across the app.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                  <Typography sx={{ color: '#f1f6ff', fontWeight: 800, fontSize: '1.1rem' }}>NVIDIA</Typography>
                  <Chip
                    size="small"
                    label="NVDA"
                    sx={{
                      bgcolor: 'rgba(34,88,182,0.42)',
                      color: '#cde1ff',
                      border: '1px solid rgba(123,164,245,0.28)',
                    }}
                  />
                </Box>

                <Grid container spacing={1} sx={{ mb: 1.3 }}>
                  {nvdaCardData.map((item) => (
                    <Grid item xs={6} key={item.label}>
                      <Box
                        sx={{
                          p: 1.12,
                          borderRadius: 2,
                          border: '1px solid rgba(118,158,235,0.22)',
                          background: 'rgba(12,29,63,0.55)',
                        }}
                      >
                        <Typography sx={{ color: '#9db8e9', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: '#f0f5ff', fontWeight: 800, fontSize: '1.08rem', mt: 0.24 }}>{item.value}</Typography>
                        <Typography sx={{ color: '#8fb5f8', fontSize: '0.74rem', mt: 0.2, lineHeight: 1.4 }}>{item.delta}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 2.5,
                    border: '1px solid rgba(123,163,238,0.26)',
                    background: 'rgba(14,32,69,0.55)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                    <Typography sx={{ color: '#9db8e9', fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      Revenue Trend (Billions USD)
                    </Typography>
                    <Typography sx={{ color: '#1bd79b', fontSize: '0.82rem', fontWeight: 700 }}>{nvdaYoyText}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, height: 92 }}>
                    {nvdaChartBars.map((bar, index) => (
                      <Box
                        key={`${bar.q}-${index}`}
                        sx={{
                          flex: 1,
                          height: `${Math.max(26, (bar.value / Math.max(...nvdaChartBars.map((row) => row.value), 1)) * 100)}%`,
                          borderRadius: 1,
                          background: index > 3
                            ? 'linear-gradient(180deg, #34d7b8 0%, #118fd8 100%)'
                            : 'linear-gradient(180deg, #4c82ff 0%, #1a4ec2 100%)',
                          opacity: 0.95,
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.7 }}>
                    {nvdaChartBars.map((bar, index) => (
                      <Typography key={`label-${bar.q}-${index}`} sx={{ color: '#88a8dc', fontSize: '0.68rem' }}>{bar.q}</Typography>
                    ))}
                  </Box>
                  <Typography sx={{ color: '#8faedf', fontSize: '0.73rem', mt: 0.65 }}>{nvdaYoyText} · sourced from app data</Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.4,
                    borderRadius: 2.5,
                    border: '1px solid rgba(123,163,238,0.24)',
                    background: 'rgba(12,28,60,0.65)',
                    p: 1.4,
                  }}
                >
                  <Typography sx={{ color: '#ecf3ff', fontWeight: 700, fontSize: '0.95rem' }}>Start free today</Typography>
                  <Typography sx={{ mt: 0.45, color: '#a7c0e9', lineHeight: 1.55, fontSize: '0.87rem' }}>
                    Build your first watchlist and compare companies in under a minute.
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<RocketLaunchIcon />}
                    onClick={() => loginWithRedirect({ appState: { returnTo: '/map' }, authorizationParams: { screen_hint: 'signup', redirect_uri: authRedirectUri } })}
                    sx={{
                      mt: 1.4,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #2060ff 0%, #1a89dd 45%, #0fbda9 100%)',
                      boxShadow: '0 12px 24px rgba(15,74,188,0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a54df 0%, #1779c7 45%, #0ca493 100%)',
                      },
                    }}
                  >
                    Create account
                  </Button>

                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => loginWithRedirect({ appState: { returnTo: '/map' }, authorizationParams: { redirect_uri: authRedirectUri } })}
                    sx={{
                      mt: 0.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#c4d8ff',
                    }}
                  >
                    I already have an account
                  </Button>

                  <Box sx={{ mt: 0.9, display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<SecurityIcon sx={{ color: '#8ec0ff !important' }} />}
                      label="Secure login"
                      sx={{
                        height: 26,
                        bgcolor: 'rgba(19,39,81,0.72)',
                        color: '#d0e2ff',
                        border: '1px solid rgba(123,164,245,0.28)',
                      }}
                    />
                    <Chip
                      label="No credit card"
                      sx={{
                        height: 26,
                        bgcolor: 'rgba(19,39,81,0.72)',
                        color: '#d0e2ff',
                        border: '1px solid rgba(123,164,245,0.28)',
                      }}
                    />
                  </Box>
                </Paper>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={{ xs: 1.4, md: 1.8 }}>
                {surfaceCards.map((card) => (
                  <Grid item xs={12} md={4} key={card.title}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(112,154,236,0.25)',
                        background: 'linear-gradient(160deg, rgba(10,22,52,0.9) 0%, rgba(8,16,35,0.92) 100%)',
                        p: 2,
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          mb: 1,
                          color: '#95c3ff',
                          background: 'rgba(40,94,190,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography sx={{ color: '#ecf3ff', fontWeight: 700, fontSize: '1rem' }}>{card.title}</Typography>
                      <Typography sx={{ mt: 0.7, color: '#acc3ea', lineHeight: 1.58, fontSize: '0.9rem' }}>{card.body}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(116,156,236,0.3)',
                  background: 'linear-gradient(160deg, rgba(9,20,47,0.92) 0%, rgba(7,14,32,0.94) 100%)',
                  p: { xs: 2, md: 2.3 },
                }}
              >
                <Typography sx={{ color: '#edf4ff', fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
                  Subscription Plans
                </Typography>
                <Typography sx={{ mt: 0.45, color: '#a9c3eb', fontSize: '0.92rem' }}>
                  Flexible rent-style access: pick a fixed term or stay subscribed until you cancel.
                </Typography>

                <Grid container spacing={1.1} sx={{ mt: 1.1 }}>
                  {subscriptionPlans.map((plan) => {
                    const isFeatured = Boolean(plan.featured);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={plan.name}>
                        <Box
                          sx={{
                            p: 1.35,
                            borderRadius: 2,
                            border: isFeatured ? '1px solid rgba(92,192,238,0.66)' : '1px solid rgba(120,161,238,0.26)',
                            background: isFeatured
                              ? 'linear-gradient(160deg, rgba(19,44,92,0.82) 0%, rgba(10,27,60,0.72) 100%)'
                              : 'rgba(13,30,66,0.6)',
                            boxShadow: isFeatured ? '0 16px 28px rgba(6,20,58,0.45)' : 'none',
                            height: '100%',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ color: '#dce9ff', fontWeight: 700, fontSize: '0.93rem' }}>{plan.name}</Typography>
                            {plan.badge && (
                              <Chip
                                size="small"
                                label={plan.badge}
                                sx={{
                                  height: 22,
                                  bgcolor: isFeatured ? 'rgba(18,189,169,0.18)' : 'rgba(40,93,186,0.24)',
                                  color: isFeatured ? '#81ebd9' : '#b8d0ff',
                                  border: isFeatured ? '1px solid rgba(24,202,180,0.36)' : '1px solid rgba(120,161,238,0.36)',
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Box>
                          <Typography sx={{ mt: 0.45, color: '#f4f8ff', fontWeight: 800, fontSize: '1.28rem' }}>{plan.price}</Typography>
                          <Typography sx={{ mt: 0.3, color: '#9ab9ea', fontSize: '0.82rem', lineHeight: 1.45 }}>{plan.note}</Typography>
                          <Button
                            fullWidth
                            variant={isFeatured ? 'contained' : 'outlined'}
                            onClick={() => handleStartCheckout(plan.key)}
                            disabled={checkoutPlanKey === plan.key}
                            sx={{
                              mt: 1,
                              borderRadius: 999,
                              py: 0.7,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.82rem',
                              borderColor: 'rgba(115,162,241,0.4)',
                              color: isFeatured ? '#e9f8ff' : '#cbe0ff',
                              background: isFeatured
                                ? 'linear-gradient(135deg, #1f63ff 0%, #19a2d6 45%, #11b89e 100%)'
                                : 'transparent',
                              '&:hover': {
                                borderColor: 'rgba(150,191,255,0.66)',
                                background: isFeatured
                                  ? 'linear-gradient(135deg, #1b58e2 0%, #168fc0 45%, #0f9f89 100%)'
                                  : 'rgba(35,85,172,0.2)',
                              },
                            }}
                          >
                            {checkoutPlanKey === plan.key ? 'Redirecting...' : 'Choose Plan'}
                          </Button>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {discountCodePanel}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  if (!hasPaidAccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'radial-gradient(circle at 20% 0%, #153a78 0%, #0a1a3c 40%, #050c1f 100%)',
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 2.5, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(116,156,236,0.3)',
              background: 'linear-gradient(160deg, rgba(9,20,47,0.92) 0%, rgba(7,14,32,0.94) 100%)',
              p: { xs: 2, md: 2.3 },
            }}
          >
            <Typography sx={{ color: '#edf4ff', fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.35rem' } }}>
              Subscription Required
            </Typography>
            <Typography sx={{ mt: 0.6, color: '#a9c3eb', fontSize: '0.94rem', lineHeight: 1.6 }}>
              Your account is authenticated but does not have an active paid subscription. Choose a plan to unlock full app access.
            </Typography>
            <Typography sx={{ mt: 0.45, color: '#8eb2ea', fontSize: '0.82rem' }}>
              Logged in as: {normalizedUserEmail || user?.sub || 'Unknown user'}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(116,156,236,0.3)',
              background: 'linear-gradient(160deg, rgba(9,20,47,0.92) 0%, rgba(7,14,32,0.94) 100%)',
              p: { xs: 2, md: 2.3 },
            }}
          >
            <Typography sx={{ color: '#edf4ff', fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
              Subscription Plans
            </Typography>
            <Grid container spacing={1.1} sx={{ mt: 0.8 }}>
              {subscriptionPlans.map((plan) => {
                const isFeatured = Boolean(plan.featured);
                return (
                  <Grid item xs={12} sm={6} md={4} key={plan.name}>
                    <Box
                      sx={{
                        p: 1.35,
                        borderRadius: 2,
                        border: isFeatured ? '1px solid rgba(92,192,238,0.66)' : '1px solid rgba(120,161,238,0.26)',
                        background: isFeatured
                          ? 'linear-gradient(160deg, rgba(19,44,92,0.82) 0%, rgba(10,27,60,0.72) 100%)'
                          : 'rgba(13,30,66,0.6)',
                        boxShadow: isFeatured ? '0 16px 28px rgba(6,20,58,0.45)' : 'none',
                        height: '100%',
                      }}
                    >
                      <Typography sx={{ color: '#dce9ff', fontWeight: 700, fontSize: '0.93rem' }}>{plan.name}</Typography>
                      <Typography sx={{ mt: 0.45, color: '#f4f8ff', fontWeight: 800, fontSize: '1.28rem' }}>{plan.price}</Typography>
                      <Typography sx={{ mt: 0.3, color: '#9ab9ea', fontSize: '0.82rem', lineHeight: 1.45 }}>{plan.note}</Typography>
                      <Button
                        fullWidth
                        variant={isFeatured ? 'contained' : 'outlined'}
                        onClick={() => handleStartCheckout(plan.key)}
                        disabled={checkoutPlanKey === plan.key}
                        sx={{
                          mt: 1,
                          borderRadius: 999,
                          py: 0.7,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          borderColor: 'rgba(115,162,241,0.4)',
                          color: isFeatured ? '#e9f8ff' : '#cbe0ff',
                          background: isFeatured
                            ? 'linear-gradient(135deg, #1f63ff 0%, #19a2d6 45%, #11b89e 100%)'
                            : 'transparent',
                          '&:hover': {
                            borderColor: 'rgba(150,191,255,0.66)',
                            background: isFeatured
                              ? 'linear-gradient(135deg, #1b58e2 0%, #168fc0 45%, #0f9f89 100%)'
                              : 'rgba(35,85,172,0.2)',
                          },
                        }}
                      >
                        {checkoutPlanKey === plan.key ? 'Redirecting...' : 'Choose Plan'}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {discountCodePanel}

            <Button
              variant="text"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              sx={{ mt: 1.2, textTransform: 'none', color: '#c4d8ff', fontWeight: 700 }}
            >
              Log out and switch account
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
